import { FC, useEffect, useMemo, useRef } from 'react';
import { parseScriptSrc } from './parseScriptSrc.ts';
import { isDntEnabled } from '@utils/dnt.ts';

export interface ThirdPartyScriptProps {
  scriptSrc?: string;
  async?: boolean;
  notForDnt?: boolean;
}

function getScriptErrorMessage(
  scriptSrc: string,
  resolvedSrc: string | null
): string | null {
  if (resolvedSrc) {
    return null;
  }

  return scriptSrc.trim()
    ? `Invalid third-party script URL or tag: ${scriptSrc}`
    : 'Third-party script not configured (configure in layout editor)';
}

function createScriptElement(src: string, async: boolean): HTMLScriptElement {
  const script = document.createElement('script');
  script.src = src;
  script.type = 'text/javascript';
  script.async = async;
  return script;
}

const ThirdPartyScript: FC<ThirdPartyScriptProps> = ({
  scriptSrc = '',
  async: loadAsync = true,
  notForDnt = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const scriptRef = useRef<HTMLScriptElement | null>(null);

  const resolvedSrc = useMemo(() => {return parseScriptSrc(scriptSrc)}, [scriptSrc]);
  const errorMessage = getScriptErrorMessage(scriptSrc, resolvedSrc);
  const dntBlocked = !notForDnt && isDntEnabled();

  useEffect(() => {
    if (dntBlocked || errorMessage) {
      if (errorMessage) {
        console.error(errorMessage);
      }
      return;
    }

    const container = containerRef.current;
    if (!container || !resolvedSrc) {
      return;
    }

    let isMounted = true;
    const script = createScriptElement(resolvedSrc, loadAsync);

    script.onerror = () => {
      if (isMounted) {
        console.error(`Failed to load script: ${resolvedSrc}`);
      }
    };

    container.appendChild(script);
    scriptRef.current = script;

    return () => {
      isMounted = false;
      const injectedScript = scriptRef.current;
      if (injectedScript?.parentNode === container) {
        container.removeChild(injectedScript);
      }
      scriptRef.current = null;
    };
  }, [dntBlocked, errorMessage, resolvedSrc, loadAsync]);

  return (
    <div
      ref={dntBlocked || errorMessage ? undefined : containerRef}
      className="third-party-script"
      {...(errorMessage && {
        dangerouslySetInnerHTML: { __html: `<!-- ${errorMessage} -->` },
      })}
    />
  );
};

export default ThirdPartyScript;
